import type { UserQuestionAnswer } from '@/frontend/pages/Dashboard/AgentChats/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import cable from '@/frontend/lib/cable'
import { agentChatApiScope } from '@/frontend/pages/Dashboard/AgentChats/api'
import type { AgentEvent } from '@/frontend/features/AgentChats/events/agentEvents'
import type {
  AgentChatMessage,
  AgentChatSessionDetail,
  AgentChatSessionSummary,
} from '@/frontend/pages/Dashboard/AgentChats/types'

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'rejected'

// Uma conversa de equipe pertence ao Command Center; qualquer outra pertence ao
// dashboard. Sessões antigas não trazem a marca e contam como do dashboard.
function belongsToThisSurface(session: { staff_access?: boolean }): boolean {
  return (session.staff_access === true) === (agentChatApiScope() === '/cc')
}
type StartSessionSource = 'digital_worker_onboarding'

interface SessionUpsertEvent {
  type: 'session_upsert'
  session: AgentChatSessionSummary
}

interface SessionDeletedEvent {
  type: 'session_deleted'
  session_id: string
}

interface MessageCreatedEvent {
  type: 'message_created'
  session_id: string
  message: AgentChatMessage
}

interface MessageSubmissionFailedEvent {
  type: 'message_submission_failed'
  session_id?: string
  client_submission_id?: string | null
  error: string
}

interface MessageSubmittedEvent {
  type: 'message_submitted'
  session_id: string
  message_id: string
  client_submission_id?: string | null
}

interface SessionStartedEvent {
  type: 'session_started'
  session: AgentChatSessionDetail
}

interface SessionLoadedEvent {
  type: 'session_loaded'
  session: AgentChatSessionDetail
}

// Run activity streamed from the OpenClaw Gateway through the fleet relay.
// Separate from `message_created`: these describe a turn in progress, while
// message events describe what was durably stored.
interface AgentEventFrame {
  type: 'agent_event'
  session_id: string
  event: AgentEvent
}

type AgentChatsEvent =
  | AgentEventFrame
  | SessionUpsertEvent
  | SessionDeletedEvent
  | MessageCreatedEvent
  | MessageSubmissionFailedEvent
  | MessageSubmittedEvent
  | SessionStartedEvent
  | SessionLoadedEvent

interface UseAgentChatsChannelOptions {
  enabled?: boolean
  organizationId: string | null
  onReconnect?: () => void
  onSessionUpsert: (session: AgentChatSessionSummary) => void
  onSessionDeleted?: (sessionId: string) => void
  onSessionStarted?: (session: AgentChatSessionDetail) => void
  onSessionLoaded?: (session: AgentChatSessionDetail) => void
  onMessageCreated: (sessionId: string, message: AgentChatMessage) => void
  onAgentEvent?: (sessionId: string, event: AgentEvent) => void
  onMessageSubmitted: (
    sessionId: string,
    messageId: string,
    clientSubmissionId: string | null,
    supportsClientSubmissionId: boolean,
  ) => void
  onSubmissionFailed: (
    sessionId: string | null,
    error: string,
    clientSubmissionId: string | null,
    supportsClientSubmissionId: boolean,
  ) => void
}

export function useAgentChatsChannel({
  enabled = true,
  organizationId,
  onReconnect,
  onSessionUpsert,
  onSessionDeleted,
  onSessionStarted,
  onSessionLoaded,
  onMessageCreated,
  onAgentEvent,
  onMessageSubmitted,
  onSubmissionFailed,
}: UseAgentChatsChannelOptions) {
  const subscriptionRef = useRef<ReturnType<typeof cable.subscriptions.create> | null>(null)
  const subscriptionVersionRef = useRef(0)
  const hasConnectedOnceRef = useRef(false)
  const callbacksRef = useRef({
    onReconnect,
    onSessionUpsert,
    onSessionDeleted,
    onSessionStarted,
    onSessionLoaded,
    onMessageCreated,
    onAgentEvent,
    onMessageSubmitted,
    onSubmissionFailed,
  })
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting')
  const connectionStatusRef = useRef<ConnectionStatus>('connecting')

  useEffect(() => {
    callbacksRef.current = {
      onReconnect,
      onSessionUpsert,
      onSessionDeleted,
      onSessionStarted,
      onSessionLoaded,
      onMessageCreated,
      onAgentEvent,
      onMessageSubmitted,
      onSubmissionFailed,
    }
  }, [
    onReconnect,
    onSessionUpsert,
    onSessionDeleted,
    onSessionStarted,
    onSessionLoaded,
    onMessageCreated,
    onAgentEvent,
    onMessageSubmitted,
    onSubmissionFailed,
  ])

  useEffect(() => {
    const subscriptionVersion = subscriptionVersionRef.current + 1
    subscriptionVersionRef.current = subscriptionVersion
    const isCurrentSubscription = () => subscriptionVersionRef.current === subscriptionVersion

    connectionStatusRef.current = 'connecting'
    setConnectionStatus('connecting')
    hasConnectedOnceRef.current = false

    if (!enabled) return

    if (!organizationId) {
      connectionStatusRef.current = 'rejected'
      setConnectionStatus('rejected')
      return
    }

    const subscription = cable.subscriptions.create(
      { channel: 'AgentChatsChannel', organization_id: organizationId },
      {
        connected() {
          if (!isCurrentSubscription()) return

          connectionStatusRef.current = 'connected'
          setConnectionStatus('connected')

          if (hasConnectedOnceRef.current) {
            callbacksRef.current.onReconnect?.()
          }

          hasConnectedOnceRef.current = true
        },
        disconnected() {
          if (!isCurrentSubscription()) return

          connectionStatusRef.current = 'disconnected'
          setConnectionStatus('disconnected')
        },
        rejected() {
          if (!isCurrentSubscription()) return

          connectionStatusRef.current = 'rejected'
          setConnectionStatus('rejected')
        },
        received(event: AgentChatsEvent) {
          if (!isCurrentSubscription()) return

          switch (event.type) {
            case 'session_upsert':
              // O broadcast vai para o stream organização+usuário, compartilhado
              // pelas duas superfícies. Sem este descarte, um admin que também é
              // membro da organização veria a conversa de equipe entrar no
              // histórico do dashboard (e vice-versa).
              if (!belongsToThisSurface(event.session)) break
              callbacksRef.current.onSessionUpsert(event.session)
              break
            case 'session_deleted':
              callbacksRef.current.onSessionDeleted?.(event.session_id)
              break
            case 'message_created':
              callbacksRef.current.onMessageCreated(event.session_id, event.message)
              break
            case 'agent_event':
              callbacksRef.current.onAgentEvent?.(event.session_id, event.event)
              break
            case 'message_submission_failed':
              callbacksRef.current.onSubmissionFailed(
                event.session_id || null,
                event.error,
                event.client_submission_id || null,
                Object.prototype.hasOwnProperty.call(event, 'client_submission_id'),
              )
              break
            case 'message_submitted':
              callbacksRef.current.onMessageSubmitted(
                event.session_id,
                event.message_id,
                event.client_submission_id || null,
                Object.prototype.hasOwnProperty.call(event, 'client_submission_id'),
              )
              break
            case 'session_started':
              callbacksRef.current.onSessionStarted?.(event.session)
              break
            case 'session_loaded':
              callbacksRef.current.onSessionLoaded?.(event.session)
              break
          }
        },
      }
    )
    subscriptionRef.current = subscription

    return () => {
      if (subscriptionVersionRef.current === subscriptionVersion) {
        subscriptionVersionRef.current += 1
      }
      if (subscriptionRef.current === subscription) {
        subscriptionRef.current = null
      }
      subscription.unsubscribe()
    }
  }, [enabled, organizationId])

  const startSession = useCallback((
    digitalWorkerId: string,
    content: string,
    agentId?: string | null,
    source?: StartSessionSource,
  ): boolean => {
    if (!subscriptionRef.current || connectionStatusRef.current !== 'connected') {
      return false
    }

    return subscriptionRef.current.perform('start_session', {
      digital_worker_id: digitalWorkerId,
      agent_id: agentId,
      content,
      source,
    }) as unknown as boolean
  }, [])

  const loadSession = useCallback((sessionId: string): boolean => {
    if (!subscriptionRef.current || connectionStatusRef.current !== 'connected') {
      return false
    }

    return subscriptionRef.current.perform('load_session', {
      session_id: sessionId,
    }) as unknown as boolean
  }, [])

  const sendMessage = useCallback((
    sessionId: string,
    content: string,
    clientSubmissionId?: string,
    pageContext?: Record<string, unknown>,
    questionAnswer?: UserQuestionAnswer,
  ): boolean => {
    if (!subscriptionRef.current || connectionStatusRef.current !== 'connected') {
      return false
    }

    return subscriptionRef.current.perform('send_message', {
      session_id: sessionId,
      content,
      client_submission_id: clientSubmissionId,
      page_context: pageContext,
      question_answer: questionAnswer,
    }) as unknown as boolean
  }, [])

  return {
    connectionStatus,
    startSession,
    loadSession,
    sendMessage,
  }
}
